import { CheckCircle2, Circle, Loader2, Wand2, Cpu } from "lucide-react";
import type { DeepPartial } from "ai";
import type { ImproveResumeLlmOutput } from "@super-resume-new/resume-engine";
import { cn } from "@super-resume-new/ui/lib/utils";
import { Badge } from "@super-resume-new/ui/components/badge";

type Props = {
  partial: DeepPartial<ImproveResumeLlmOutput> | undefined;
  isStreaming: boolean;
};

const STEPS = [
  { key: "target_profile", label: "Target Keywords", desc: "The list the rewrite hunts for" },
  {
    key: "keyword_assignments",
    label: "Keyword Placement",
    desc: "Which bullet each term lands in",
  },
  { key: "personal", label: "Personal Information", desc: "Contact details & social handles" },
  { key: "headline", label: "Professional Title", desc: "ATS role alignment title" },
  { key: "summary", label: "Executive Summary", desc: "High-impact opening profile" },
  { key: "skills", label: "Core Skills & Tech", desc: "Categorized skill matrices" },
  { key: "experience", label: "Work Experience", desc: "Quantified achievement bullets" },
  { key: "projects", label: "Key Projects", desc: "Technical contributions & outcomes" },
  { key: "education", label: "Education & History", desc: "Academic background & degrees" },
  { key: "certifications", label: "Certifications", desc: "Verified credentials & honors" },
  { key: "changes", label: "ATS Optimization Log", desc: "Score improvements & feedback" },
] as const;

function isFieldPresent(
  partial: DeepPartial<ImproveResumeLlmOutput> | undefined,
  key: string,
): boolean {
  // These two stream *before* the resume — they are the early signal.
  if (key === "target_profile") return (partial?.target_profile?.keywords?.length ?? 0) > 0;
  if (key === "keyword_assignments") return (partial?.keyword_assignments?.length ?? 0) > 0;
  const doc = partial?.optimized_resume as Record<string, unknown> | undefined;
  if (!doc) return false;
  if (key === "changes") return (partial?.changes as unknown[])?.length > 0;
  const val = doc[key];
  if (Array.isArray(val)) return val.length > 0;
  return val !== undefined && val !== null;
}

export function StreamProgress({ partial, isStreaming }: Props) {
  const completedCount = STEPS.filter((s) => isFieldPresent(partial, s.key)).length;
  const keywordCount = partial?.target_profile?.keywords?.length ?? 0;
  const placedCount = partial?.keyword_assignments?.length ?? 0;
  const status =
    keywordCount === 0
      ? "Reading the job description…"
      : partial?.optimized_resume
        ? `Rewriting bullets around ${placedCount} placed keywords…`
        : placedCount === 0
          ? `Found ${keywordCount} target keywords — planning placement…`
          : `Placing keywords… ${placedCount} of ${keywordCount} assigned to bullets`;
  const progressPercent = Math.min(
    100,
    Math.max(8, Math.round((completedCount / STEPS.length) * 100)),
  );

  return (
    <div className="flex flex-col gap-5 w-full">
      {/* Header & Progress Bar Card */}
      <div className="relative overflow-hidden rounded-xl border border-brand/20 bg-card p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="relative flex size-10 items-center justify-center rounded-xl bg-brand/15 border border-brand/30 text-brand-foreground">
              <Wand2 className="size-5" aria-hidden="true" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground tracking-tight">
                  Optimizing Resume
                </h3>
                <Badge
                  variant="secondary"
                  className="border-brand/30 bg-brand/10 text-brand-foreground text-xs px-2 py-0.5 font-mono"
                >
                  {progressPercent}%
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                <Cpu className="size-3.5 text-muted-foreground animate-spin" />
                {status}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4 relative w-full h-2 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-violet-500 transition duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Section Pipeline List */}
      <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4 shadow-sm">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1 pb-1 border-b border-border mb-1">
          Progress Checklist
        </span>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {STEPS.map((step, idx) => {
            const done = isFieldPresent(partial, step.key);
            const active =
              isStreaming &&
              !done &&
              (completedCount === idx || (idx === 0 && completedCount === 0));

            return (
              <div
                key={step.key}
                className={cn(
                  "flex items-center justify-between rounded-xl px-3.5 py-2.5 text-xs transition border",
                  done && "border-emerald-500/20 bg-emerald-500/5 text-foreground font-medium",
                  active &&
                    "border-brand/40 bg-brand/10 text-brand-foreground font-semibold shadow-sm ring-1 ring-brand/20",
                  !done && !active && "border-border bg-muted/20 text-muted-foreground/60",
                )}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  {done ? (
                    <CheckCircle2
                      size={16}
                      className="text-emerald-500 shrink-0"
                      aria-hidden="true"
                    />
                  ) : active ? (
                    <Loader2
                      size={16}
                      className="animate-spin text-brand shrink-0"
                      aria-hidden="true"
                    />
                  ) : (
                    <Circle
                      size={16}
                      className="text-muted-foreground/40 shrink-0"
                      aria-hidden="true"
                    />
                  )}
                  <div className="flex flex-col truncate">
                    <span className="truncate">{step.label}</span>
                    <span className="text-[10px] text-muted-foreground/70 font-normal truncate">
                      {step.desc}
                    </span>
                  </div>
                </div>

                {done && (
                  <span className="text-[10px] font-mono text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 shrink-0">
                    Done
                  </span>
                )}
                {active && (
                  <span className="text-[10px] font-mono text-brand-foreground bg-brand/15 px-1.5 py-0.5 shrink-0 rounded border border-border">
                    Processing
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
